import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
} from "react";

import { useQuery } from "@tanstack/react-query";

import { initUseQuery } from "simplydo/core/query";
import { OpenAPI } from "simplydo/interfaces";

import api from "api";

/**
 * The IdeaContext provides the useIdea hooks with the current ids required to fetch the idea and its child.
 */
export const IdeaContext = createContext<{
  ideaId: string;
  setIdeaId: Dispatch<SetStateAction<string>>;
  childId: string | undefined | null;
  setChildId: Dispatch<SetStateAction<string | undefined | null>>;
}>({
  ideaId: "",
  setIdeaId: () => {},
  childId: undefined,
  setChildId: () => {},
});

export const useIdeaIdState = () => {
  const ideaContext = useContext(IdeaContext);

  return [ideaContext.ideaId, ideaContext.setIdeaId] as const;
};

export const useChildIdState = () => {
  const ideaContext = useContext(IdeaContext);
  return [ideaContext.childId, ideaContext.setChildId] as const;
};

type UseIdeaOptions<T> = {
  select?: (data: OpenAPI.GET<"/ideas/{id}">["response"]) => T;
};

const useIdeaQuery = <_X, T = undefined>(
  ideaId: string,
  options?: UseIdeaOptions<T>,
) => {
  return useQuery(
    initUseQuery(api.ideas.get, [ideaId], {
      isEnabled: !!ideaId,
      select: options?.select,
    }),
  );
};

/**
 * The useIdea hook will always return the anchoring parent idea, no matter whether an idea is merged or not. This is useful for merged ideas when you need to access the parent idea data.
 */
export const useIdea = <_X, T = undefined>(options?: UseIdeaOptions<T>) => {
  const [ideaId, setParentId] = useIdeaIdState();
  const [childId, setChildId] = useChildIdState();

  const ideaQuery = useIdeaQuery<_X, T>(ideaId, options);
  const ideaData = ideaQuery.data as OpenAPI.GET<"/ideas/{id}">["response"];

  useEffect(() => {
    if (ideaData?.parent) {
      setParentId(ideaData.parent);
      setChildId(ideaData?._id);
      return;
    }
    if (ideaData?.children && ideaData.children.length > 0) {
      setChildId((prev) => (!prev ? ideaData.children![0] : prev));
    }
  }, [
    childId,
    ideaData?.children,
    ideaData?.parent,
    ideaData?._id,
    setChildId,
    setParentId,
  ]);

  return [
    ideaData as T extends undefined ? OpenAPI.GET<"/ideas/{id}">["response"]
    : T,
    ideaQuery,
  ] as const;
};

/**
 * The useChildIdea hook will return either a child of an idea (if the idea is merged) otherwise it will also return the idea itself. This way this hook can be used to render the content of a child or the idea itself without requiring conditional logic in the component.
 */
export const useChildIdea = <_X, T = undefined>(
  options?: UseIdeaOptions<T>,
) => {
  const [ideaId] = useIdeaIdState();
  const [childId] = useChildIdState();

  const ideaQuery = useIdeaQuery(childId ?? ideaId, options);

  return [
    ideaQuery.data as T extends undefined ?
      OpenAPI.GET<"/ideas/{id}">["response"]
    : T,
    ideaQuery,
  ] as const;
};
